"use client";

import { useState } from "react";
import {
  Checkbox,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  type AminoFormat,
  OrfView,
  SingleFrameView,
  SixFrameView,
} from "@/components/tools/biology-genetics/translator-parts";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import { parseFasta } from "@/lib/tool-kit/biology-genetics/fasta";
import { cleanNucleotides } from "@/lib/tool-kit/biology-genetics/iupac";
import { findOrfs } from "@/lib/tool-kit/biology-genetics/orf";

/** English strings. A translated content file overrides these through `ui`. */
export const DNA_TO_PROTEIN_TRANSLATOR_STRINGS = {
  inputLabel: "DNA or RNA sequence — plain text or FASTA",
  inputHint:
    "Spaces, line breaks, numbers and lower case are fine. Several FASTA records are translated one by one. Ambiguity codes (N, R, Y…) are handled.",
  inputPlaceholder: ">my_sequence\nATGGCCCTGTGGATGCGC…",
  exampleHeader: "NM_000207.3 Homo sapiens insulin (INS), coding sequence",
  tryExample: "Try an example",
  modeLabel: "What do you want to do?",
  modeTranslate: "Translate a reading frame",
  modeOrfs: "Find open reading frames",
  frameLabel: "Reading frame",
  frame1: "+1 — forward, from base 1",
  frame2: "+2 — forward, from base 2",
  frame3: "+3 — forward, from base 3",
  frameMinus1: "−1 — reverse complement, from its base 1",
  frameMinus2: "−2 — reverse complement, from its base 2",
  frameMinus3: "−3 — reverse complement, from its base 3",
  frameAll: "All six frames",
  formatLabel: "Amino acid format",
  formatOne: "One-letter (MAL…)",
  formatThree: "Three-letter (Met-Ala-Leu…)",
  toStop: "Stop at the first stop codon",
  fromStart: "Begin at the first ATG",
  minLabel: "Minimum ORF length (amino acids)",
  minHint: "75 nucleotides, the default in NCBI ORFfinder, is 25 amino acids.",
  bothStrands: "Search both strands",
  openEnded: "Include ORFs that run off the end of the sequence",
  results: "Results",
  unnamed: "Sequence {n}",
  lengthNt: "Sequence length",
  proteinLength: "Protein length",
  stopCodons: "Stop codons",
  unitNt: "nt",
  unitAa: "aa",
  leftover:
    "one: {count} base is left over after the last complete codon and was not translated.|other: {count} bases are left over after the last complete codon and were not translated.",
  proteinHeading: "Protein",
  mrnaHeading: "mRNA — the strand read, with U for T",
  copyProtein: "Copy protein",
  copyFasta: "Copy as FASTA",
  copyMrna: "Copy mRNA",
  copyAllFasta: "Copy all six frames as FASTA",
  copyOrfFasta: "Copy ORF proteins as FASTA",
  copied: "Copied!",
  highlightLabel:
    "Sequence split into codons, with start and stop codons highlighted",
  highlightLegend:
    "Green marks an ATG codon; red marks a stop codon (TAA, TAG, TGA).",
  truncated:
    "Showing the first {count} bases only; the results above cover the whole sequence.",
  codonTable: "Codon-by-codon table",
  numberColumn: "#",
  codonColumn: "Codon",
  threeColumn: "Three-letter",
  oneColumn: "One-letter",
  frameHeading: "Frame {frame}",
  frameStops: "one: {count} stop codon|other: {count} stop codons",
  orfsFound:
    "one: {count} open reading frame found, longest first.|other: {count} open reading frames found, longest first.",
  noOrfs:
    "No ATG-to-stop ORF of at least {min} amino acids was found. Lower the minimum, or include ORFs that run off the end.",
  frameColumn: "Frame",
  startColumn: "Start",
  endColumn: "End",
  ntColumn: "Length (nt)",
  aaColumn: "Length (aa)",
  proteinColumn: "Protein",
  noStop: "no stop",
  orfRowsLimit:
    "The table lists the first {count} ORFs; the FASTA copy includes all of them.",
  orfMapHeading: "ORF map",
  orfMapLabel: "Sequence with open reading frames highlighted",
  orfLegend:
    "Amber marks ORFs on the strand you entered; blue marks ORFs on the reverse strand.",
  prompt: "Paste a DNA or RNA sequence to translate it.",
  errorNone: "No nucleotide letters were found in that text.",
  ignored: "Ignored characters that are not nucleotide codes: {list}.",
  mixedTU: "The sequence contains both T and U; U is read as T.",
  recordsLimit: "Only the first {count} records are shown.",
  tooLong: "Only the first {count} bases of each record are used.",
} as const;

type FrameChoice = "1" | "2" | "3" | "-1" | "-2" | "-3" | "all";
type Mode = "translate" | "orfs";

const MAX_RECORDS = 20;
const MAX_BASES = 2_000_000;
const EXAMPLE_SEQUENCE =
  "ATGGCCCTGTGGATGCGCCTCCTGCCCCTGCTGGCGCTGCTGGCCCTCTGGGGACCTGACCCAGCCGCAG" +
  "CCTTTGTGAACCAACACCTGTGCGGCTCACACCTGGTGGAAGCTCTCTACCTAGTGTGCGGGGAACGAGG" +
  "CTTCTTCTACACACCCAAGACCCGCCGGGAGGCAGAGGACCTGCAGGTGGGGCAGGTGGAGCTGGGCGGG" +
  "GGCCCTGGTGCAGGCAGCCTGCAGCCCTTGGCCCTGGAGGGGTCCCTGCAGAAGCGTGGCATTGTGGAAC" +
  "AATGCTGTACCAGCATCTGCTCCCTCTACCAGCTGGAGAACTACTGCAACTAG";
const FRAME_VALUES = ["1", "2", "3", "-1", "-2", "-3", "all"] as const;

export default function DnaToProteinTranslatorWidget() {
  const strings = useToolUi(DNA_TO_PROTEIN_TRANSLATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState(
    `>${strings.exampleHeader}\n${EXAMPLE_SEQUENCE}`
  );
  const [mode, setMode] = useState<Mode>("translate");
  const [frame, setFrame] = useState<FrameChoice>("1");
  const [format, setFormat] = useState<AminoFormat>("one");
  const [toStop, setToStop] = useState(false);
  const [fromStart, setFromStart] = useState(false);
  const [minRaw, setMinRaw] = useState("25");
  const [both, setBoth] = useState(true);
  const [openEnded, setOpenEnded] = useState(false);

  const records = parseFasta(text);
  const cleaned = records.map((record) => cleanNucleotides(record.sequence));
  const invalid = [...new Set(cleaned.flatMap((entry) => entry.invalid))];
  const mixed = cleaned.some((entry) => entry.hasT && entry.hasU);
  const usable = records
    .map((record, index) => ({
      name: record.name || t(strings.unnamed, { n: fmt(index + 1) }),
      sequence: cleaned[index].sequence
        .slice(0, MAX_BASES)
        .replaceAll("U", "T"),
      truncated: cleaned[index].sequence.length > MAX_BASES,
    }))
    .filter((entry) => entry.sequence !== "");
  const minAa = Math.max(1, parseIntegerInput(minRaw) ?? 25);

  const frameOptions = FRAME_VALUES.map((value, index) => ({
    value,
    label: [
      strings.frame1,
      strings.frame2,
      strings.frame3,
      strings.frameMinus1,
      strings.frameMinus2,
      strings.frameMinus3,
      strings.frameAll,
    ][index],
  }));

  function renderRecord(entry: (typeof usable)[number]) {
    if (mode === "orfs") {
      const orfs = findOrfs(entry.sequence, {
        minAminoAcids: minAa,
        searchBothStrands: both,
        includeOpenEnded: openEnded,
      });
      return (
        <OrfView
          fmt={fmt}
          locale={locale}
          minAminoAcids={minAa}
          name={entry.name}
          orfs={orfs}
          sequence={entry.sequence}
          strings={strings}
        />
      );
    }
    if (frame === "all") {
      return (
        <SixFrameView
          fmt={fmt}
          format={format}
          fromStart={fromStart}
          locale={locale}
          name={entry.name}
          sequence={entry.sequence}
          strings={strings}
          toStop={toStop}
        />
      );
    }
    return (
      <SingleFrameView
        fmt={fmt}
        format={format}
        frame={Number(frame)}
        fromStart={fromStart}
        locale={locale}
        name={entry.name}
        sequence={entry.sequence}
        strings={strings}
        toStop={toStop}
      />
    );
  }

  let message: string | null = null;
  if (text.trim() === "") {
    message = strings.prompt;
  } else if (usable.length === 0) {
    message = strings.errorNone;
  }

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="d2p-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        rows={8}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            setText(`>${strings.exampleHeader}\n${EXAMPLE_SEQUENCE}`)
          }
          type="button"
        >
          {strings.tryExample}
        </button>
      </div>
      <PillGroup
        label={strings.modeLabel}
        name="d2p-mode"
        onChange={setMode}
        options={[
          { value: "translate", label: strings.modeTranslate },
          { value: "orfs", label: strings.modeOrfs },
        ]}
        value={mode}
      />
      {mode === "translate" ? (
        <div className={`${CARD} space-y-4`}>
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              id="d2p-frame"
              label={strings.frameLabel}
              onChange={setFrame}
              options={frameOptions}
              value={frame}
            />
            <SelectField
              id="d2p-format"
              label={strings.formatLabel}
              onChange={setFormat}
              options={[
                { value: "one", label: strings.formatOne },
                { value: "three", label: strings.formatThree },
              ]}
              value={format}
            />
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Checkbox
              checked={toStop}
              label={strings.toStop}
              onChange={setToStop}
            />
            <Checkbox
              checked={fromStart}
              label={strings.fromStart}
              onChange={setFromStart}
            />
          </div>
        </div>
      ) : (
        <div className={`${CARD} space-y-4`}>
          <div className="max-w-xs">
            <TextField
              hint={strings.minHint}
              id="d2p-min"
              label={strings.minLabel}
              mono
              numeric
              onChange={setMinRaw}
              value={minRaw}
            />
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Checkbox
              checked={both}
              label={strings.bothStrands}
              onChange={setBoth}
            />
            <Checkbox
              checked={openEnded}
              label={strings.openEnded}
              onChange={setOpenEnded}
            />
          </div>
        </div>
      )}
      {invalid.length > 0 ? (
        <Notice>{t(strings.ignored, { list: invalid.join(" ") })}</Notice>
      ) : null}
      {mixed ? <Notice>{strings.mixedTU}</Notice> : null}
      {usable.some((entry) => entry.truncated) ? (
        <Notice>{t(strings.tooLong, { count: fmt(MAX_BASES) })}</Notice>
      ) : null}
      {message ? <Notice>{message}</Notice> : null}
      {usable.length > 0 ? (
        <div className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          {usable.slice(0, MAX_RECORDS).map((entry) => (
            <section
              className="space-y-4"
              key={`${entry.name}-${entry.sequence.length}`}
            >
              {usable.length > 1 || records[0]?.header ? (
                <h3 className={WIDGET_H3}>{entry.name}</h3>
              ) : null}
              {renderRecord(entry)}
            </section>
          ))}
          {usable.length > MAX_RECORDS ? (
            <Notice>
              {t(strings.recordsLimit, { count: fmt(MAX_RECORDS) })}
            </Notice>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
